import { validateQuestion } from 'components/questions/questionValidation';
import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'components/ui/input';
import { QuestionField, QuestionValidationProvider, useQuestionValidationFeedback } from 'components/questions/QuestionField';
import { useTranslation } from 'react-i18next';
import { Loader2, Crown } from 'lucide-react';
import { Button } from 'components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import type { Subject } from 'modules/subject/types';
import type { CreateBankQuestionData } from 'modules/questionBank/api';
import type { BankQuestion } from 'modules/questionBank/types';
import type { QuestionType } from '@examing/types';
import {
  DISPATCHED_QUESTION_TYPES,
  type QuestionTypeData,
} from 'components/questions/QuestionTypeEditor';
import { QuestionAuthoringFields } from './QuestionAuthoringFields';
import { QuestionTopicInput } from 'components/questions/QuestionTopicInput';
import { useQuestionTopicField } from 'components/questions/questionTopic';

import { isAdvancedQuestionType } from 'pages/QuestionBank/questionTypeMeta';
import { FEATURES, useFeature, useOpenUpgradeForFeature } from 'modules/entitlements';

// Question-type options (basic first, then advanced — the latter are Growth+).
const TYPE_OPTIONS: { value: QuestionType; labelKey: string }[] = [
  { value: 'MULTIPLE_CHOICE', labelKey: 'single_choice' },
  { value: 'MULTIPLE_RESPONSE', labelKey: 'multiple_choice' },
  { value: 'TRUE_FALSE', labelKey: 'true_false' },
  { value: 'SHORT_ANSWER', labelKey: 'short_answer' },
  { value: 'MATCHING', labelKey: 'matching' },
  { value: 'FILL_IN_BLANKS', labelKey: 'fill_in_blanks' },
  { value: 'OPEN_ENDED', labelKey: 'open_ended' },
  { value: 'REORDER', labelKey: 'reorder' },
  { value: 'PASSAGE', labelKey: 'passage' },
];

// Type-specific authoring fields carried separately from the basic form state.
const TYPE_DATA_KEYS: (keyof QuestionTypeData)[] = [
  'answerMode', 'acceptedAnswers', 'maxWords', 'matchingOptions', 'blanks', 'reorderItems', 'correctOrder',
  'mediaUrl', 'aiImageDescription', 'rubric',
  'passageText', 'passageTitle', 'subQuestions', 'pages',
];

function extractTypeData(source: Partial<QuestionTypeData> | undefined): QuestionTypeData {
  const out: QuestionTypeData = source ? {} : { answerMode: 'exact' };
  if (!source) return out;
  for (const key of TYPE_DATA_KEYS) {
    if (source[key] !== undefined) (out as Record<string, unknown>)[key] = source[key];
  }
  return out;
}

interface Props {
  subjects: Subject[];
  initialData?: BankQuestion;
  defaultSubjectId?: string;
  // `opts.stay` is set by "Save & add another": the page saves but keeps the
  // author on the form instead of navigating back to the bank list.
  onSubmit: (data: CreateBankQuestionData, opts?: { stay?: boolean }) => Promise<void>;
  onCancel: () => void;
  isSubmitting: boolean;
}

type QuestionOption = { id: string; value: string };

// Default options/answer for a given type — shared by the initial state, the
// type-change reset, and the "save & add another" reset so they can't drift.
function defaultOptionsForType(type: QuestionType): QuestionOption[] {
  if (type === 'TRUE_FALSE') {
    return [
      { id: 'opt_true', value: 'True' },
      { id: 'opt_false', value: 'False' },
    ];
  }
  if (type === 'MATCHING') return [];
  return [
    { id: 'opt_0', value: '' },
    { id: 'opt_1', value: '' },
    { id: 'opt_2', value: '' },
    { id: 'opt_3', value: '' },
  ];
}

function defaultAnswerForType(type: QuestionType): string | string[] {
  if (type === 'TRUE_FALSE') return 'opt_true';
  if (type === 'MATCHING') return '';
  return type === 'MULTIPLE_RESPONSE' ? [] : '';
}

export const BankQuestionForm: React.FC<Props> = ({
  subjects,
  initialData,
  defaultSubjectId,
  onSubmit,
  onCancel,
  isSubmitting,
}) => {
  const { t } = useTranslation();
  // Advanced (non-basic) question types are a Growth+ capability.
  const canAdvancedTypes = useFeature(FEATURES.ADVANCED_QUESTION_TYPES);
  const openUpgrade = useOpenUpgradeForFeature();
  const isLockedType = (value: string) => !canAdvancedTypes && isAdvancedQuestionType(value);
  const [subjectId, setSubjectId] = useState(initialData?.subjectId ?? defaultSubjectId ?? '');
  const [type, setType] = useState<QuestionType>(initialData?.type ?? 'MULTIPLE_CHOICE');
  const [prompt, setPrompt] = useState(initialData?.prompt ?? '');
  const [typeData, setTypeData] = useState<QuestionTypeData>(() => extractTypeData(initialData));
  const [options, setOptions] = useState<QuestionOption[]>(
    initialData?.options ?? [
      { id: 'opt_0', value: '' },
      { id: 'opt_1', value: '' },
      { id: 'opt_2', value: '' },
      { id: 'opt_3', value: '' },
    ],
  );
  const [correctAnswer, setCorrectAnswer] = useState<string | string[] | Record<string, string>>(
    initialData?.correctAnswer ?? '',
  );
  const [explanation, setExplanation] = useState(initialData?.explanation ?? '');
  const topic = useQuestionTopicField(initialData?.topic);
  const [points, setPoints] = useState(initialData?.points ?? 1);
  const [difficulty, setDifficulty] = useState<'easy' | 'medium' | 'hard'>(
    (initialData?.difficulty as 'easy' | 'medium' | 'hard') ?? 'medium',
  );
  const [matchPairs, setMatchPairs] = useState<Array<{ key: string; value: string }>>(
    initialData?.matchPairs ?? [
      { key: '', value: '' },
      { key: '', value: '' },
    ],
  );

  // Reset options/answer when type changes. TRUE_FALSE/MATCHING always reseed
  // (their option shape is fixed); other types only reseed in create mode so an
  // edited question keeps its authored options.
  useEffect(() => {
    if (!initialData || initialData.type !== type) {
      setOptions(defaultOptionsForType(type));
      setCorrectAnswer(defaultAnswerForType(type));
    }
  }, [type]); // eslint-disable-line react-hooks/exhaustive-deps

  // Option/answer-key handlers now live in QuestionAuthoringFields, which owns
  // the authoring body for both this form and the review dialog.
  const isDispatchedType = DISPATCHED_QUESTION_TYPES.includes(type);

  // Clear the authoring fields after "save & add another", keeping subject,
  // type, and difficulty so the author can rattle off several similar questions.
  const resetForNext = () => {
    resetValidation();
    setPrompt('');
    setExplanation('');
    topic.reset();
    setTypeData({ answerMode: 'exact' });
    setOptions(defaultOptionsForType(type));
    setCorrectAnswer(defaultAnswerForType(type));
    setMatchPairs([
      { key: '', value: '' },
      { key: '', value: '' },
    ]);
  };

  const validation = validateQuestion({ subjectId, type, prompt, options, correctAnswer, matchPairs, points, ...typeData });
  const { formRef, revealErrors, resetValidation, visibleIssues } = useQuestionValidationFeedback(validation.issues);
  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);
  const submittingRef = useRef(false);
  const handleSubmit = async (continueAfter = false) => {
    revealErrors();
    if (!validation.isValid || isSubmitting || submittingRef.current) return;
    submittingRef.current = true;
    setSaving(true);
    setSaveError('');
    try {
    await onSubmit(
      {
        subjectId,
        type,
        prompt: (type === 'PASSAGE' ? typeData.passageText ?? prompt : prompt).trim(),
        options: ['MULTIPLE_CHOICE', 'MULTIPLE_RESPONSE', 'TRUE_FALSE'].includes(type)
          ? options
          : undefined,
        matchPairs: type === 'MATCHING' ? matchPairs : undefined,
        // Extended types carry their answer key in the type-specific fields, not
        // in correctAnswer (which defaults to '' server-side).
        correctAnswer: isDispatchedType ? '' : correctAnswer,
        points,
        explanation: explanation.trim() || undefined,
        difficulty,
        ...topic.patch,
        ...typeData,
      },
      { stay: continueAfter },
    );
    // onSubmit (mutateAsync) rejects on failure, so we only reach here on a
    // successful save — safe to clear the form for the next question.
    if (continueAfter) resetForNext();
    } catch {
      setSaveError(t('failed_to_save_draft_changes'));
    } finally {
      submittingRef.current = false;
      setSaving(false);
    }
  };

  return (
    <QuestionValidationProvider issues={visibleIssues}>
    <div ref={formRef} className="space-y-5 py-2" {...(saving || isSubmitting ? { inert: true, "aria-busy": true } : {})}>
      {/* Subject + Type row */}
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="text-sm font-medium mb-1 block">{t('subject')} *</label>
          <Select value={subjectId} onValueChange={setSubjectId}>
            <QuestionField name="subjectId"><SelectTrigger aria-label={t('subject')}>
              <SelectValue placeholder={t('select_subject')} />
            </SelectTrigger></QuestionField>
            <SelectContent>
              {subjects.map((s) => (
                <SelectItem key={s.id} value={s.id}>
                  {s.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <label className="text-sm font-medium mb-1 block">{t('question_type')} *</label>
          <Select
            value={type}
            onValueChange={(value) => {
              // Selecting a locked advanced type opens the upgrade dialog and
              // leaves the current (basic) type in place instead of switching.
              if (isLockedType(value)) {
                openUpgrade(FEATURES.ADVANCED_QUESTION_TYPES);
                return;
              }
              setType(value as QuestionType);
              // The picture description belongs to the question, not the type.
              setTypeData((previous) => ({ answerMode: 'exact', ...(previous.aiImageDescription ? { aiImageDescription: previous.aiImageDescription } : {}) }));
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {TYPE_OPTIONS.map((opt) => (
                <SelectItem key={opt.value} value={opt.value}>
                  <span className="flex items-center gap-1.5">
                    {t(opt.labelKey)}
                    {isLockedType(opt.value) && (
                      <Crown className="size-3 text-amber-500" aria-hidden />
                    )}
                  </span>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Stem, type-specific editor, options/answer key, and explanation —
          shared with the generated-questions review dialog so both edit through
          one implementation. */}
      <QuestionAuthoringFields
        value={{ type, prompt, options, correctAnswer, matchPairs, explanation, typeData }}
        onChange={(patch) => {
          if (patch.prompt !== undefined) setPrompt(patch.prompt);
          if (patch.options !== undefined) setOptions(patch.options);
          if (patch.correctAnswer !== undefined) setCorrectAnswer(patch.correctAnswer);
          if (patch.matchPairs !== undefined) setMatchPairs(patch.matchPairs);
          if (patch.explanation !== undefined) setExplanation(patch.explanation);
          if (patch.typeData !== undefined) setTypeData(patch.typeData);
        }}
        uploadContext={subjectId ? `question-bank/${subjectId}` : 'question-bank'}
      />

      <QuestionTopicInput value={topic.value} onChange={topic.onChange} />

      {/* Difficulty */}
      <div>
        <label className="text-sm font-medium mb-1 block">{t('difficulty')}</label>
        <Select
          value={difficulty}
          onValueChange={(v) => setDifficulty(v as 'easy' | 'medium' | 'hard')}
        >
          <SelectTrigger>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="easy">{t('easy')}</SelectItem>
            <SelectItem value="medium">{t('medium')}</SelectItem>
            <SelectItem value="hard">{t('hard')}</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <label className="block space-y-1 text-sm">
        <span>{t('points')}</span>
        <QuestionField name="points"><Input type="number" min={0.01} step={0.01} value={points} onChange={(event) => setPoints(Number(event.target.value))} /></QuestionField>
      </label>
      {saveError && <p role="alert" className="text-sm text-destructive">{saveError}</p>}

      {/* Actions */}
      <div className="flex justify-end gap-2 pt-2 border-t">
        <Button variant="outline" onClick={onCancel}>
          {t('cancel')}
        </Button>
        {!initialData && (
          <Button
            variant="secondary"
            onClick={() => handleSubmit(true)}
            disabled={saving || isSubmitting}
          >
            {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            {t('save_and_add_another')}
          </Button>
        )}
        <Button onClick={() => handleSubmit(false)} disabled={saving || isSubmitting}>
          {isSubmitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
          {initialData ? t('save_changes') : t('add_to_bank')}
        </Button>
      </div>
    </div>
    </QuestionValidationProvider>
  );
};
